// Printable Form WH-347 — U.S. Department of Labor Davis-Bacon Certified Payroll.
//
// Worker lines, wages, hours, and the certifying signer are now pulled from the
// REAL payroll data path (weekly_payroll_entry x crew_roster, via
// GET /bead-pm/projects/:id/payroll?week=). There is NO hardcoded roster and NO
// fabricated wage determination — the wage determination / location is derived
// from the project's award state + county.
//
// The signable form (with the perjury Statement of Compliance) is only rendered
// when the selected week is backed by entered payroll data. With no data we show
// an honest empty state and no Print button, so a false federal record can never
// be produced from fabricated numbers.
//
// Source: Form WH-347 per 29 CFR 5.5 / 40 USC 3141.

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

type Worker = {
  entry_id: string;
  crew_id: string;
  name: string;
  employee_id: string | null;
  classification: string;
  withholding_exemptions: number;
  wage_rate_per_hour: number;
  fringe_per_hour: number;
  hours_sun: number; hours_mon: number; hours_tue: number;
  hours_wed: number; hours_thu: number; hours_fri: number; hours_sat: number;
  total_hours: number; st_hours: number; ot_hours: number;
  gross_st: number; gross_ot: number; fringe_total: number; gross_total: number;
  fica: number; federal: number; state_tax: number; other: number;
  total_deductions: number; net_pay: number;
};

type PayrollResponse = {
  project: {
    id: string; project_code: string; project_name: string;
    county: string | null; state: string | null;
    prime_contractor: string | null; contract_ref: string | null;
    wage_determination: string | null;
  };
  contractor: {
    name: string | null; address: string | null;
    officer_name: string | null; officer_title: string | null;
  };
  weeks: Array<{ week_ending: string; worker_count: number; total_hours: number }>;
  week: string | null;
  payroll_number: number | null;
  workers: Worker[];
  totals: {
    workers: number; total_hours: number; gross_pay: number;
    total_deductions: number; net_pay: number;
  } | null;
};

const fmtUSD = (n: number) => `$${(n ?? 0).toFixed(2)}`;
const fmtUSDInt = (n: number) => `$${Math.round(n ?? 0).toLocaleString()}`;

function parseLocalDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}
const fmtDate = (d: any) => {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString() : '—';
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function WH347Page() {
  // The route param carries the week_ending (opened from the Davis-Bacon list);
  // legacy links that passed an obligation id are simply ignored and we fall
  // back to the project's latest week.
  const { obligationId } = useParams();
  const [searchParams] = useSearchParams();
  const projectParam = searchParams.get('project') ?? '';
  const weekFromRoute = obligationId && DATE_RE.test(obligationId) ? obligationId : '';

  const [projectId, setProjectId] = useState(projectParam);
  const [week, setWeek] = useState(weekFromRoute);

  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['bead-projects-wh347'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Auto-select the first real project once the list loads (no stale hardcoded id).
  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const { data: payroll, isLoading } = useQuery<PayrollResponse>({
    queryKey: ['wh347-payroll', projectId, week],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/payroll${week ? `?week=${week}` : ''}`),
    enabled: !!projectId,
  });

  // Keep the week selector in sync with whatever the server resolved (latest).
  useEffect(() => {
    if (payroll?.week && !week) setWeek(payroll.week);
  }, [payroll, week]);

  const project = payroll?.project;
  const contractor = payroll?.contractor;
  const workers = payroll?.workers ?? [];
  const totals = payroll?.totals ?? null;

  const location = useMemo(() => {
    if (!project) return '—';
    const parts = [project.project_name];
    if (project.county) parts.push(`${project.county} County`);
    if (project.state) parts.push(project.state);
    return parts.filter(Boolean).join(' · ');
  }, [project]);

  const wageDeterminationLabel = project?.wage_determination
    ?? (project?.state
        ? `Per executed subgrant contract wage determination for ${project.county ?? '—'} County, ${project.state}`
        : 'Per executed subgrant contract wage determination');

  // Reusable project + week switchers (shown in loading, empty, and print bar).
  const controls = (
    <div style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', fontFamily: 'system-ui' }}>
      <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#475569' }}>Project</span>
        <select value={projectId} onChange={e => { setProjectId(e.target.value); setWeek(''); }}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12, minWidth: 220 }}>
          {projects.map((p: any) => (
            <option key={p.id} value={p.id}>{p.project_code} — {p.project_name}</option>
          ))}
        </select>
      </label>
      {(payroll?.weeks?.length ?? 0) > 0 && (
        <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#475569' }}>Week ending</span>
          <select value={payroll?.week ?? ''} onChange={e => setWeek(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12 }}>
            {payroll!.weeks.map(w => (
              <option key={w.week_ending} value={w.week_ending}>
                {fmtDate(w.week_ending)} ({w.worker_count} workers)
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );

  if (isLoading || !payroll) {
    return (
      <div style={{ padding: 40, fontFamily: 'system-ui' }}>
        <div style={{ marginBottom: 16 }}>{controls}</div>
        <div style={{ color: '#64748b' }}>Loading certified payroll…</div>
      </div>
    );
  }

  // HONEST EMPTY STATE — never render a signable form from empty/fake data.
  if (workers.length === 0) {
    return (
      <div style={{ padding: 40, fontFamily: 'system-ui', maxWidth: 820, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>{controls}</div>
        <div style={{
          padding: 28, border: '1px dashed #cbd5e1', borderRadius: 8,
          background: '#f8fafc', color: '#475569', lineHeight: 1.6,
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
            No certified payroll recorded
          </div>
          <p style={{ margin: '0 0 10px' }}>
            There are no weekly payroll entries for
            {' '}<b>{project?.project_code ?? 'this project'}</b>
            {week ? <> for the week ending <b>{fmtDate(week)}</b></> : null}.
          </p>
          <p style={{ margin: 0 }}>
            A Form WH-347 can only be generated from entered payroll data. Add
            weekly payroll (crew hours &amp; wages) for this project, then return
            here to produce the signable certified-payroll form.
          </p>
        </div>
      </div>
    );
  }

  const payrollNum = payroll.payroll_number ?? 1;
  const days = (w: Worker) => [w.hours_sun, w.hours_mon, w.hours_tue, w.hours_wed, w.hours_thu, w.hours_fri, w.hours_sat];

  const grandHours = totals?.total_hours ?? 0;
  const grandGross = totals?.gross_pay ?? 0;
  const grandNet = totals?.net_pay ?? 0;
  const grandDed = totals?.total_deductions ?? 0;

  const signerName = contractor?.officer_name?.trim() || '';
  const signerTitle = contractor?.officer_title?.trim() || '';
  const contractorName = contractor?.name?.trim() || project?.prime_contractor || '(contractor name not set)';
  const contractorAddr = contractor?.address?.trim() || '(address not set)';

  return (
    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '40px 50px',
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 11, color: '#0f172a', lineHeight: 1.4, background: 'white',
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          @page { size: letter landscape; margin: 0.4in; }
        }
        table.payroll { border-collapse: collapse; width: 100%; font-size: 9px; }
        table.payroll th, table.payroll td { border: 1px solid #475569; padding: 3px 4px; text-align: left; vertical-align: top; }
        table.payroll thead th { background: #e2e8f0; font-weight: 700; font-size: 8px; text-transform: uppercase; letter-spacing: .03em; text-align: center; }
        .day-cell { width: 22px; text-align: center !important; font-variant-numeric: tabular-nums; }
        .num-cell { text-align: right !important; font-variant-numeric: tabular-nums; white-space: nowrap; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 20, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap', fontFamily: 'system-ui' }}>
        {controls}
        <span>Use your browser's <b>Print → Save as PDF</b> (landscape) to export this WH-347.</span>
        <button onClick={() => window.print()}
          style={{ padding: '4px 12px', background: '#0f172a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}>Print</button>
      </div>

      {/* Form header */}
      <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10, color: '#475569' }}>U.S. Department of Labor · Wage and Hour Division</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>PAYROLL · Form WH-347</div>
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>(For Contractor's Optional Use; See Instructions, Form WH-347 Inst.)</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9 }}>OMB No. 1235-0008</div>
            <div style={{ fontSize: 9 }}>Expires: 09/30/2026</div>
          </div>
        </div>
      </div>

      {/* Project / Contractor metadata */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: 10 }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(1) NAME OF CONTRACTOR / SUBCONTRACTOR</div>
              <div style={{ fontWeight: 700 }}>{contractorName}</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(2) ADDRESS</div>
              <div>{contractorAddr}</div>
            </td>
          </tr>
          <tr>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(3) PAYROLL NO.</div>
              <div style={{ fontWeight: 700 }}>{String(payrollNum).padStart(3, '0')}</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(4) FOR WEEK ENDING</div>
              <div style={{ fontWeight: 700 }}>{fmtDate(payroll.week)}</div>
            </td>
          </tr>
          <tr>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(5) PROJECT AND LOCATION</div>
              <div>{location}</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(6) PROJECT OR CONTRACT NO.</div>
              <div>{project?.project_code}{project?.contract_ref ? ` · ${project.contract_ref}` : ''}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Worker rows */}
      <table className="payroll">
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: '14%' }}>(1) Name & Individual ID</th>
            <th rowSpan={2} style={{ width: 30 }}>(2) No. of W/H Exemptions</th>
            <th rowSpan={2} style={{ width: '11%' }}>(3) Work Classification</th>
            <th colSpan={7}>(4) Day & Date</th>
            <th rowSpan={2}>O/T</th>
            <th rowSpan={2}>S/T</th>
            <th rowSpan={2}>(5) Total Hours</th>
            <th rowSpan={2}>(6) Rate of Pay (incl. fringe)</th>
            <th colSpan={2}>(7) Gross Amount Earned</th>
            <th colSpan={4}>(8) Deductions</th>
            <th rowSpan={2}>(9) Net Wages Paid</th>
          </tr>
          <tr>
            <th className="day-cell">S</th>
            <th className="day-cell">M</th>
            <th className="day-cell">T</th>
            <th className="day-cell">W</th>
            <th className="day-cell">T</th>
            <th className="day-cell">F</th>
            <th className="day-cell">S</th>
            <th>This Project</th>
            <th>All Projects</th>
            <th>FICA</th>
            <th>Fed</th>
            <th>State</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {workers.map((r, i) => (
            <tr key={r.entry_id ?? i}>
              <td>
                {r.name}
                {r.employee_id ? <div style={{ fontSize: 7, color: '#64748b' }}>ID {r.employee_id}</div> : null}
              </td>
              <td className="day-cell">{r.withholding_exemptions ?? 0}</td>
              <td>{r.classification}</td>
              {days(r).map((h, j) => (
                <td key={j} className="day-cell">{h || ''}</td>
              ))}
              <td className="num-cell">{r.ot_hours || '—'}</td>
              <td className="num-cell">{r.st_hours}</td>
              <td className="num-cell">{r.total_hours}</td>
              <td className="num-cell">{fmtUSD(r.wage_rate_per_hour)}<div style={{ fontSize: 7, color: '#64748b' }}>+{fmtUSD(r.fringe_per_hour)} fr</div></td>
              <td className="num-cell">{fmtUSD(r.gross_total)}</td>
              <td className="num-cell">{fmtUSD(r.gross_total)}</td>
              <td className="num-cell">{fmtUSD(r.fica)}</td>
              <td className="num-cell">{fmtUSD(r.federal)}</td>
              <td className="num-cell">{fmtUSD(r.state_tax)}</td>
              <td className="num-cell">{fmtUSD(r.total_deductions)}</td>
              <td className="num-cell" style={{ fontWeight: 700 }}>{fmtUSD(r.net_pay)}</td>
            </tr>
          ))}
          <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
            <td colSpan={3}>WEEKLY TOTAL</td>
            <td className="day-cell" colSpan={7}>—</td>
            <td className="num-cell" colSpan={2}>—</td>
            <td className="num-cell">{grandHours}</td>
            <td>—</td>
            <td className="num-cell">{fmtUSDInt(grandGross)}</td>
            <td className="num-cell">{fmtUSDInt(grandGross)}</td>
            <td colSpan={4} className="num-cell">{fmtUSDInt(grandDed)}</td>
            <td className="num-cell">{fmtUSDInt(grandNet)}</td>
          </tr>
        </tbody>
      </table>

      {/* Statement of Compliance */}
      <div style={{ marginTop: 20, padding: 14, border: '2px solid #0f172a', background: '#f8fafc' }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>STATEMENT OF COMPLIANCE</div>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          Date <b>{new Date().toLocaleDateString()}</b>. I,
          {' '}<b>{signerName || '________________________'}</b>
          {signerTitle ? <>, <b>{signerTitle}</b></> : null}, do hereby state:
          (1) That I pay or supervise the payment of the persons employed by {contractorName}
          {' '}on the {project?.project_name} BEAD project; that during the payroll period ending
          {' '}<b>{fmtDate(payroll.week)}</b>, all persons employed on said project have been paid
          the full weekly wages earned, that no rebates have been or will be made either directly
          or indirectly to or on behalf of said contractor from the full weekly wages earned by any
          person and that no deductions have been made either directly or indirectly from the full
          wages earned by any person, other than permissible deductions as defined in Regulations,
          Part 3 (29 C.F.R. Subtitle A).
        </p>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          (2) That any payrolls otherwise under this contract required to be submitted for the above
          period are correct and complete; that the wage rates for laborers or mechanics contained
          therein are not less than the applicable wage rates contained in any wage determination
          incorporated into the contract; that the classifications set forth therein for each laborer
          or mechanic conform with the work performed.
        </p>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          (3) That any apprentices employed in the above period are duly registered in a bona fide
          apprenticeship program registered with a State apprenticeship agency recognized by the
          Bureau of Apprenticeship and Training, U.S. Department of Labor.
        </p>
        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 28 }} />
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Signature of Contractor or Subcontractor</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 28, display: 'flex', alignItems: 'flex-end', fontSize: 10 }}>{signerTitle}</div>
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Title</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 8, color: '#94a3b8', textAlign: 'center', fontFamily: 'system-ui' }}>
        Generated by Milestone Build · {new Date().toLocaleString()} · {wageDeterminationLabel} ·
        Populated from entered weekly payroll · For SBO submission upon signature
      </div>
    </div>
  );
}
